import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useAppSelector} from '@/store';
import {getDigitalCodesList, deleteDigitalCodes} from '@/services/digitalCodes';
import {getComposedData} from '@/services/transformers/digitalCodesTransformer';
import {REWARD_STATUS} from '@/utils/constants/rewardsConstants';
import {useDataTable} from '@/hooks/useDataTable-hook';
import {getDateWithTimeString} from '@/helpers/shadcn/utils';
import {buildQueryParams} from '@/utils/commons';

const DIGITAL_CODES_PAGE_SIZE = 10;

export function useDigitalCodesCard({rewardStatus = REWARD_STATUS.DRAFT}) {
  const [allRows, setAllRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);
  const [tableKey, setTableKey] = useState(0);

  // Use shared data-table hook for pagination, filters, and sorting (API-based), like useSalesTargets
  const {
    searchArr,
    sortingObj,
    pageIndex,
    setPageIndex,
    setSearchArry,
    setSortingObj,
    handleResetAll: tableHandleResetAll
  } = useDataTable();

  const rewardId = useAppSelector((state) => state.rewards?.formData?.rewardId);
  const rewardName = useAppSelector((state) => state.rewards?.formData?.name);

  const queryParams = useMemo(() => {
    return buildQueryParams({
      pageSize: DIGITAL_CODES_PAGE_SIZE,
      page: pageIndex || 0,
      ...(sortingObj?.column && {
        sortBy: sortingObj.column,
        order: sortingObj.direction
      }),
      ...(rewardId && {rewardId})
    });
  }, [pageIndex, rewardId, sortingObj?.column, sortingObj?.direction]);

  const loadData = useCallback(async () => {
    // Do not call the API unless a rewardId is available
    if (!rewardId) {
      setAllRows([]);
      setTotalRecords(0);
      return;
    }

    setLoading(true);
    try {
      const filters = Array.isArray(searchArr)
        ? searchArr
        : searchArr
          ? [searchArr]
          : [];
      const res = await getDigitalCodesList(queryParams, filters);
      const composed = getComposedData(res);
      setAllRows(composed || []);
      const apiTotal =
        typeof res?.totalRecords === 'number' ? res.totalRecords : res?.total;
      setTotalRecords(
        typeof apiTotal === 'number' ? apiTotal : (composed || []).length
      );
    } finally {
      setLoading(false);
    }
  }, [queryParams, rewardId, searchArr]);

  useEffect(() => {
    mountedRef.current = true;
    loadData();
    return () => {
      mountedRef.current = false;
    };
  }, [loadData]);

  // With API-based sorting, processed rows are just what the API returned
  const processed = useMemo(() => allRows, [allRows]);

  const pageSize = DIGITAL_CODES_PAGE_SIZE;
  const pagedRecords = useMemo(() => processed, [processed]);

  const onResetAll = useCallback(() => {
    setSelectedRows([]);
    setConfirmOpen(false);
    setPendingDeleteIds([]);
    // Reset shared table state (filters, pageIndex) similar to useSalesTargets
    tableHandleResetAll();
  }, [tableHandleResetAll]);

  const handleSorting = useCallback(
    ({column, direction}) => {
      if (!column) return;

      // Delegate to useDataTable so queryParams (sortBy/order) and API calls stay in sync
      setSortingObj({column, direction});
    },
    [setSortingObj]
  );

  const onRowSelect = useCallback((rows) => {
    setSelectedRows(rows || []);
  }, []);

  const showDownloadEnabled = !!rewardId;
  const showUploadEnabled =
    !!rewardId &&
    !(
      rewardStatus === REWARD_STATUS.EXPIRED ||
      rewardStatus === REWARD_STATUS.REVOKED
    );
  const anySelected = selectedRows.length > 0;
  const selectionHasCustomer = selectedRows.some((r) => !!r.customerId);
  const selectionHasUsed = selectedRows.some(
    (r) => r.used === true || r.used === 'Yes'
  );
  const canBulkDelete =
    anySelected && !selectionHasCustomer && !selectionHasUsed;

  const handleDownloadTemplate = useCallback(() => {
    const headers = [
      'Reward ID',
      'Reward Name',
      'Code',
      'Expiry date',
      'Code Used'
    ];
    const valuesRow = [
      rewardId ? String(rewardId).replaceAll(',', ' ') : '',
      rewardName ? String(rewardName).replaceAll(',', ' ') : '',
      '',
      '',
      ''
    ];
    const csv = [headers.join(','), valuesRow.join(',')].join('\n');
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `DigitalCodesTemplate_${getDateWithTimeString()}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [rewardId, rewardName]);

  const handleBulkDeleteClick = useCallback(() => {
    const ids = selectedRows.map((r) => r.id);
    setPendingDeleteIds(ids);
    setConfirmOpen(true);
  }, [selectedRows]);

  const handleSingleDeleteClick = useCallback((row) => {
    if (row.customerId) return;
    if (row.used === true || row.used === 'Yes') return;
    setPendingDeleteIds([row.id]);
    setConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    try {
      setLoading(true);
      await deleteDigitalCodes({rewardId, digitalCodes: pendingDeleteIds});
      // Reset table state (including pageIndex back to 0) and let the
      // effect + useDataTable reload data for the first page.
      tableHandleResetAll();
    } finally {
      setLoading(false);
      setSelectedRows([]);
      setPendingDeleteIds([]);
      setConfirmOpen(false);
      setTableKey((prev) => prev + 1);
    }
  }, [pendingDeleteIds, rewardId, tableHandleResetAll]);

  const hasActiveFilters = Array.isArray(searchArr) && searchArr.length > 0;

  return {
    // data
    allRows,
    processed,
    pagedRecords,
    totalRecords,
    pageSize,

    // selection & paging
    pageIndex,
    setPageIndex,
    selectedRows,

    // sorting & search
    sortingObj,
    // pass through to CustomDataTable; it will call this with single-field searchObj,
    // and useDataTable will build/maintain the filters array (searchArr) for the API
    setSearchObj: setSearchArry,
    handleSorting,
    onResetAll,

    // actions
    onRowSelect,
    handleDownloadTemplate,
    handleBulkDeleteClick,
    handleSingleDeleteClick,

    // delete modal
    confirmOpen,
    setConfirmOpen,
    handleConfirmDelete,

    // flags
    loading,
    showDownloadEnabled,
    showUploadEnabled,
    canBulkDelete,
    hasActiveFilters,

    // external
    rewardId,
    tableKey
  };
}
